import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ThinTextField,
  AddButton,
  ThinDropdown,
  MultiSelectDropdown,
} from "../../../components/common/GeneralComponent";
import { toast } from "react-toastify";
import { fetchData, submitFormWithErrors } from "../../../utils/functions";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getGrantTypeConfig } from "../grantTypeConfig";
import { SDG_OPTIONS } from "../sdgOptions";

const BeneficiaryDetails = ({
  grantId,
  onNext,
  isEditMode,
  showNextOnly,
  showUpdateOnly,
  onStoreFormData,
  onValidationChange,
  initialData,
  onGrantUpdated,
  grantType = "sme",
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cfg = getGrantTypeConfig(grantType);
  const [loading, setLoading] = useState(false);
  const [isCheckingCnic, setIsCheckingCnic] = useState(false);
  const [businessProposalFile, setBusinessProposalFile] = useState(null);

  const formatCNIC = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length <= 5) {
      return cleaned;
    } else if (cleaned.length <= 12) {
      return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
    } else {
      return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 12)}-${cleaned.slice(
        12,
        13
      )}`;
    }
  };

  const isValidCNIC = (value) => {
    if (!value) return false;
    const cleanCnic = String(value).replace(/[-\s]/g, "");
    return /^[0-9]{13}$/.test(cleanCnic);
  };

  const formatContactNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length <= 4) {
      return cleaned;
    } else if (cleaned.length <= 11) {
      return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
    } else {
      return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 11)}`;
    }
  };

  const isValidPhone = (value) => {
    if (!value) return false;
    const cleaned = String(value).replace(/[-\s]/g, "");
    return /^03[0-9]{9}$/.test(cleaned);
  };

  const validationSchema = Yup.object({
    application_id: Yup.string().required("Application ID is required"),
    company_name: Yup.string().required("Company Name is required"),
    applicant_full_name: Yup.string().required(
      "Applicant Full Name is required"
    ),
    father_husband_name: Yup.string(),
    cnic_number: Yup.string()
      .test(
        "cnic-format",
        "CNIC must be in format: 12345-1234567-1",
        function (value) {
          if (!value) return false;
          return isValidCNIC(value);
        }
      )
      .required("CNIC Number is required"),
    gender: Yup.string().required("Gender is required"),
    date_of_birth: Yup.date()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      )
      .nullable()
      .max(new Date(), "Date of Birth cannot be in the future"),
    person_with_disability: Yup.boolean().required(
      "Person with Disability is required"
    ),
    marital_status: Yup.string(),
    contact_number: Yup.string()
      .test(
        "phone-format",
        "Contact number must be 11 digits starting with 03 (format: 03XX-XXXXXXX)",
        function (value) {
          if (!value) return false;
          return isValidPhone(value);
        }
      )
      .required("Contact Number is required"),
    email: Yup.string()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Please enter a valid email address"
      )
      .email("Invalid email format")
      .required("Email is required"),
    address: Yup.string().required("Address is required"),
    business_sector: Yup.string().required("Business Sector is required"),
    business_category: Yup.string().required("Business Category is required"),
    business_sub_type: Yup.string().nullable(),
    business_sector_other: Yup.string().when("business_sector", {
      is: "Other/Specify",
      then: (schema) => schema.required("Please specify other business sector").matches(/^[^0-9]*$/, "Numbers are not allowed"),
      otherwise: (schema) => schema.nullable(),
    }),
    business_description: Yup.string().required(
      "Business Description is required"
    ),
    date_of_selection: Yup.date()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      )
      .nullable()
      .max(new Date(), "Date of Selection cannot be in the future"),
  });

  const formik = useFormik({
    initialValues: {
      application_id: initialData?.application_id || "",
      company_name: initialData?.company_name || "",
      applicant_full_name: initialData?.applicant_full_name || "",
      father_husband_name: initialData?.father_husband_name || "",
      cnic_number: initialData?.cnic_number || "",
      gender: initialData?.gender || "",
      date_of_birth: initialData?.date_of_birth || "",
      person_with_disability: initialData?.person_with_disability ?? "",
      marital_status: initialData?.marital_status || "",
      contact_number: initialData?.contact_number || "",
      email: initialData?.email || "",
      address: initialData?.address || "",
      business_sector: initialData?.business_sector || "",
      sdg_interventions: initialData?.sdg_interventions || [],
      business_category: initialData?.business_category || "",
      business_sub_type: initialData?.business_sub_type || "",
      business_sector_other: initialData?.business_sector_other || "",
      business_description: initialData?.business_description || "",
      date_of_selection: initialData?.date_of_selection || "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      await handleFormSubmit(values);
    },
  });

  const handleFormSubmit = async (values) => {
    setLoading(true);
    try {
      if (isEditMode) {
        const appIdValid = await validateApplicationIdInDatabase(values.application_id, grantId);
        if (!appIdValid) { setLoading(false); return; }

        if (values.cnic_number && isValidCNIC(values.cnic_number)) {
          const cnicCheck = await fetchData(
            `${cfg.apiBase}/validate-cnic/${encodeURIComponent(values.cnic_number)}`,
            "GET", null, {}, { exclude_id: grantId, grant_type: cfg.grantTypeValue }, navigate, "", dispatch
          );
          if (cnicCheck?.exists) {
            toast.error("A grant with this CNIC already exists");
            formik.setFieldError("cnic_number", "This CNIC is already registered");
            setLoading(false);
            return;
          }
        }

        // Only make API call in edit mode
        let putBody;
        if (businessProposalFile instanceof File) {
          putBody = new FormData();
          Object.entries(values).forEach(([key, val]) => {
            if (val !== null && val !== undefined) {
              putBody.append(key, Array.isArray(val) ? JSON.stringify(val) : val);
            }
          });
          putBody.append("business_proposal_file", businessProposalFile);
        } else {
          putBody = { ...values };
        }

        const response = await fetchData(
          `${cfg.apiBase}/${grantId}`,
          "PUT",
          putBody,
          {},
          {},
          navigate,
          "",
          dispatch
        );

        if (response) {
          toast.success("Beneficiary details updated successfully");
          if (onGrantUpdated) {
            onGrantUpdated();
          }
        }
      } else {
        // For create mode, just store data locally
        const dataWithFile = {
          ...values,
          business_proposal_file: businessProposalFile,
        };

        if (onStoreFormData) {
          onStoreFormData(dataWithFile);
        }

        toast.success("Beneficiary details saved locally");
        if (onNext) onNext();
      }
    } catch (error) {
      console.error("Error saving beneficiary details:", error);
      toast.error("Failed to save beneficiary details");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const maxSize = 1 * 1024 * 1024;

      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload a PDF or Word document");
        e.target.value = "";
        return;
      }

      if (file.size > maxSize) {
        toast.error("File size should be less than 1MB");
        e.target.value = "";
        return;
      }

      setBusinessProposalFile(file);

      const updatedFormData = {
        ...formik.values,
        business_proposal_file: file,
      };

      if (onStoreFormData) {
        onStoreFormData(updatedFormData);
      }
    }
  };

  const validateApplicationIdInDatabase = async (applicationId, excludeId = null) => {
    if (!applicationId) return true;
    try {
      const response = await fetchData(
        `${cfg.apiBase}/validate-application-id/${encodeURIComponent(applicationId)}`,
        "GET",
        null,
        {},
        excludeId
          ? { exclude_id: excludeId, grant_type: cfg.grantTypeValue }
          : { grant_type: cfg.grantTypeValue },
        navigate,
        "",
        dispatch
      );
      if (response && response.exists) {
        toast.error("A grant with this Application ID already exists");
        formik.setFieldError("application_id", "This Application ID is already registered");
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error validating Application ID:", error);
      return true;
    }
  };

  const handleNextClick = async () => {
    if (loading || isCheckingCnic) return;

    // Run Yup validation first to catch format errors before DB calls
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(
        Object.keys(formik.values).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {})
      );
      return;
    }

    // Check Application ID uniqueness
    if (formik.values.application_id) {
      const appIdValid = await validateApplicationIdInDatabase(formik.values.application_id);
      if (!appIdValid) return;
    }

    // Check CNIC uniqueness
    if (formik.values.cnic_number && isValidCNIC(formik.values.cnic_number)) {
      const cnicIsValid = await validateCnicInDatabase(formik.values.cnic_number);
      if (!cnicIsValid) return;
    }

    formik.validateForm().then((errors) => {
      if (Object.keys(errors).length > 0) {
        formik.setTouched(
          Object.keys(formik.values).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {})
        );
        return;
      }

      // Store the form data with the file
      const dataWithFile = {
        ...formik.values,
        business_proposal_file: businessProposalFile,
      };

      if (onStoreFormData) {
        onStoreFormData(dataWithFile);
      }

      toast.success("Beneficiary details saved");

      setTimeout(() => {
        if (onNext) {
          onNext();
        }
      }, 100);
    });
  };

  const validateCnicInDatabase = async (cnicValue) => {
    if (!cnicValue || !isValidCNIC(cnicValue)) return;

    setIsCheckingCnic(true);
    try {
      const response = await fetchData(
        `${cfg.apiBase}/validate-cnic/${encodeURIComponent(cnicValue)}`,
        "GET",
        null,
        {},
        { grant_type: cfg.grantTypeValue },
        navigate,
        "",
        dispatch
      );

      if (response && response.exists) {
        toast.error("A grant with this CNIC already exists");
        formik.setFieldError("cnic_number", "This CNIC is already registered");
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error validating CNIC:", error);
      // Don't show error toast for validation check, just continue
      return true;
    } finally {
      setIsCheckingCnic(false);
    }
  };

  useEffect(() => {
    if (onValidationChange) {
      const timeoutId = setTimeout(() => {
        onValidationChange(formik.isValid);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [formik.isValid, onValidationChange]);

  useEffect(() => {
    if (onStoreFormData && !isEditMode) {
      const timeoutId = setTimeout(() => {
        const dataToStore = { ...formik.values };
        if (businessProposalFile) {
          dataToStore.business_proposal_file = businessProposalFile;
        }
        onStoreFormData(dataToStore);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [formik.values, businessProposalFile, onStoreFormData, isEditMode]);

  useEffect(() => {
    if (initialData?.business_proposal_file instanceof File) {
      setBusinessProposalFile(initialData.business_proposal_file);
    }
  }, [initialData]);

  const MaritalStatusOptions = [
    { label: "Married", value: "Married" },
    { label: "Widow/ Widower", value: "Widow/ Widower" },
    { label: "Divorced", value: "Divorced" },
    { label: "Separated", value: "Separated" },
    { label: "Never Married", value: "Never Married" },
  ];

  const genderOptions = [
    { label: "Male", value: "Male" },
    { label: "Female", value: "Female" },
    { label: "Transgender", value: "Transgender" },
  ];

  const businessCategoryOptions = [
    { label: "New", value: "New" },
    { label: "Existing", value: "Existing" },
    { label: "Expansion", value: "Expansion" },
  ];

  const businessSectorOptions = [
    { label: "Aquaculture & Fisheries", value: "Aquaculture & Fisheries" },
    {
      label: "Agri-Food & Beverage Processing",
      value: "Agri-Food & Beverage Processing",
    },
    {
      label: "Livestock, Poultry & Meat Value Chain",
      value: "Livestock, Poultry & Meat Value Chain",
    },
    {
      label: "Textiles, Apparel & Handicrafts",
      value: "Textiles, Apparel & Handicrafts",
    },
    {
      label: "Automotive & Light Engineering",
      value: "Automotive & Light Engineering",
    },
    {
      label: "Construction Materials & Prefabrication",
      value: "Construction Materials & Prefabrication",
    },
    {
      label: "Renewable Energy & Energy Services",
      value: "Renewable Energy & Energy Services",
    },
    { label: "Health & Care Services", value: "Health & Care Services" },
    {
      label: "Education, Training & EdTech",
      value: "Education, Training & EdTech",
    },
    {
      label: "Tourism, Hospitality & Creative Services",
      value: "Tourism, Hospitality & Creative Services",
    },
    { label: "ICT & Digital Services", value: "ICT & Digital Services" },
    {
      label: "Waste, Recycling & Circular Economy",
      value: "Waste, Recycling & Circular Economy",
    },
    {
      label: "Retail, Distribution & Service Trades",
      value: "Retail, Distribution & Service Trades",
    },
    { label: "Other/Specify", value: "Other/Specify" },
  ];

  const handleBusinessSectorChange = (e) => {
    const value = e.target.value;
    formik.setValues((prev) => ({
      ...prev,
      business_sector: value,
      business_sub_type: "",
      ...(value === "Other/Specify" && { business_sector_other: "" }),
    }));
  };

  return (
    <form onSubmit={submitFormWithErrors(formik)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <ThinTextField
            label="Application ID"
            name="application_id"
            value={formik.values.application_id}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.application_id && formik.errors.application_id
            }
            placeholder="Enter Application ID"
          />
          {formik.touched.application_id && formik.errors.application_id && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.application_id}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="Company Name"
            name="company_name"
            value={formik.values.company_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.company_name && formik.errors.company_name}
            placeholder="Enter Company Name"
          />
          {formik.touched.company_name && formik.errors.company_name && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.company_name}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="Full Name of Applicant/Grant Recipient"
            name="applicant_full_name"
            value={formik.values.applicant_full_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.applicant_full_name &&
              formik.errors.applicant_full_name
            }
            placeholder="Enter Full Name"
          />
          {formik.touched.applicant_full_name &&
            formik.errors.applicant_full_name && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.applicant_full_name}
              </div>
            )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <ThinTextField
            label="Father/Husband Name"
            name="father_husband_name"
            value={formik.values.father_husband_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.father_husband_name &&
              formik.errors.father_husband_name
            }
            placeholder="Enter Father/Husband Name"
            isRequired={false}
          />
          {formik.touched.father_husband_name &&
            formik.errors.father_husband_name && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.father_husband_name}
              </div>
            )}
        </div>

        <div>
          <ThinTextField
            label="CNIC Number"
            name="cnic_number"
            value={formik.values.cnic_number}
            onChange={(e) => {
              const cleanValue = e.target.value.replace(/\D/g, "");
              if (cleanValue.length <= 13) {
                const formatted = formatCNIC(cleanValue);
                formik.setFieldValue("cnic_number", formatted);

                // Clear previous CNIC error when user starts typing
                if (formik.errors.cnic_number) {
                  formik.setFieldError("cnic_number", undefined);
                }
              }
            }}
            onBlur={async (e) => {
              formik.handleBlur(e);
              // Validate CNIC in database when user finishes typing
              if (isValidCNIC(e.target.value)) {
                await validateCnicInDatabase(e.target.value);
              }
            }}
            error={formik.touched.cnic_number && formik.errors.cnic_number}
            placeholder="XXXXX-XXXXXXX-X"
            maxLength={15}
            disabled={isCheckingCnic}
          />
          {isCheckingCnic && (
            <div className="text-blue-500 text-sm mt-1">
              Checking CNIC availability...
            </div>
          )}
          {formik.touched.cnic_number && formik.errors.cnic_number && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.cnic_number}
            </div>
          )}
        </div>

        <div>
          <ThinDropdown
            label="Gender"
            id="gender-dropdown"
            options={genderOptions}
            value={formik.values.gender}
            onChange={formik.handleChange}
            name="gender"
            error={formik.touched.gender && formik.errors.gender}
          />
          {formik.touched.gender && formik.errors.gender && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.gender}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <ThinTextField
            label="Date of Birth"
            name="date_of_birth"
            type="date"
            value={formik.values.date_of_birth}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.date_of_birth && formik.errors.date_of_birth}
            max={new Date().toISOString().split("T")[0]}
            isRequired={false}
          />
          {formik.touched.date_of_birth && formik.errors.date_of_birth && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.date_of_birth}
            </div>
          )}
        </div>

        <div>
          <ThinDropdown
            label="Person with Disability"
            id="person-with-disability-dropdown"
            options={[
              { label: "Yes", value: "true" },
              { label: "No", value: "false" },
            ]}
            value={
              formik.values.person_with_disability === "" ||
              formik.values.person_with_disability === null ||
              formik.values.person_with_disability === undefined
                ? ""
                : String(formik.values.person_with_disability)
            }
            onChange={(e) =>
              formik.setFieldValue(
                "person_with_disability",
                e.target.value === "" ? "" : e.target.value === "true"
              )
            }
            onBlur={formik.handleBlur}
            name="person_with_disability"
            error={
              formik.touched.person_with_disability &&
              formik.errors.person_with_disability
            }
          />
          {formik.touched.person_with_disability &&
            formik.errors.person_with_disability && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.person_with_disability}
              </div>
            )}
        </div>

        <div>
          <ThinDropdown
            label="Marital Status"
            id="marital-status-dropdown"
            options={MaritalStatusOptions}
            value={formik.values.marital_status}
            onChange={formik.handleChange}
            name="marital_status"
            error={
              formik.touched.marital_status && formik.errors.marital_status
            }
            required={false}
          />
          {formik.touched.marital_status && formik.errors.marital_status && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.marital_status}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="Contact Number"
            name="contact_number"
            value={formik.values.contact_number}
            onChange={(e) => {
              const cleanValue = e.target.value.replace(/\D/g, "");
              if (
                cleanValue === "" ||
                cleanValue === "0" ||
                cleanValue.startsWith("03")
              ) {
                if (cleanValue.length <= 11) {
                  const formatted = formatContactNumber(cleanValue);
                  formik.setFieldValue("contact_number", formatted);
                }
              }
            }}
            onBlur={formik.handleBlur}
            error={
              formik.touched.contact_number && formik.errors.contact_number
            }
            placeholder="03XX-XXXXXXX"
            maxLength={12}
          />
          {formik.touched.contact_number && formik.errors.contact_number && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.contact_number}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="Email"
            name="email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && formik.errors.email}
            placeholder="example@email.com"
          />
          {formik.touched.email && formik.errors.email && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.email}
            </div>
          )}
        </div>

        <div>
          <ThinDropdown
            label="Business Category"
            id="business-category-dropdown"
            options={businessCategoryOptions}
            value={formik.values.business_category}
            onChange={formik.handleChange}
            name="business_category"
            error={
              formik.touched.business_category &&
              formik.errors.business_category
            }
          />
          {formik.touched.business_category &&
            formik.errors.business_category && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.business_category}
              </div>
            )}
        </div>

        <div>
          <ThinDropdown
            label="Business Sector"
            id="business-sector-dropdown"
            options={businessSectorOptions}
            value={formik.values.business_sector}
            onChange={handleBusinessSectorChange}
            name="business_sector"
            error={
              formik.touched.business_sector && formik.errors.business_sector
            }
          />
          {formik.touched.business_sector && formik.errors.business_sector && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.business_sector}
            </div>
          )}
        </div>

        <div>
          {formik.values.business_sector !== "Other/Specify" ? (
            <>
              <ThinTextField
                label="Business Sub Type"
                name="business_sub_type"
                isRequired={false}
                value={formik.values.business_sub_type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.business_sub_type &&
                  formik.errors.business_sub_type
                }
                placeholder="Enter business sub type"
              />
              {formik.touched.business_sub_type &&
                formik.errors.business_sub_type && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.business_sub_type}
                  </div>
                )}
            </>
          ) : (
            <>
              <ThinTextField
                label="Specify Other Business Sector"
                name="business_sector_other"
                value={formik.values.business_sector_other}
                onChange={(e) =>
                  formik.setFieldValue("business_sector_other", e.target.value.replace(/[0-9]/g, ""))
                }
                onBlur={formik.handleBlur}
                error={
                  formik.touched.business_sector_other &&
                  formik.errors.business_sector_other
                }
                placeholder="Please specify other business sector"
              />
              {formik.touched.business_sector_other &&
                formik.errors.business_sector_other && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.business_sector_other}
                  </div>
                )}
            </>
          )}
        </div>

        <div>
          <MultiSelectDropdown
            label="SDG Interventions"
            options={SDG_OPTIONS}
            selectedValues={formik.values.sdg_interventions}
            onChange={(values) =>
              formik.setFieldValue("sdg_interventions", values)
            }
            placeholder="Select SDG Interventions"
          />
        </div>

        <div>
          <ThinTextField
            label="Address"
            name="address"
            value={formik.values.address}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.address && formik.errors.address}
            placeholder="Enter complete address"
          />
          {formik.touched.address && formik.errors.address && (
            <div className="text-red-500 text-sm mt-1">
              {formik.errors.address}
            </div>
          )}
        </div>

        <div>
          <ThinTextField
            label="Business Description"
            name="business_description"
            value={formik.values.business_description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.business_description &&
              formik.errors.business_description
            }
            placeholder="Describe the business in detail"
          />
          {formik.touched.business_description &&
            formik.errors.business_description && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.business_description}
              </div>
            )}
        </div>

        <div>
          <ThinTextField
            label="Date of Selection"
            name="date_of_selection"
            type="date"
            value={formik.values.date_of_selection}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.date_of_selection &&
              formik.errors.date_of_selection
            }
            max={new Date().toISOString().split("T")[0]}
            isRequired={false}
          />
          {formik.touched.date_of_selection &&
            formik.errors.date_of_selection && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.date_of_selection}
              </div>
            )}
        </div>

        <div>
          <label className="text-[0.9rem] text-[#565656] mb-1 font-[InterLight] block">
            Business Proposal File Upload
          </label>
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            className="text-[15px] font-[InterLight] text-[#565656] bg-[#ebeef2] focus:bg-[#dde2e9] py-2 px-4 outline-none border-[#dde2e9] border rounded-[0.35rem] w-full"
          />
          {businessProposalFile instanceof File && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
              <p className="text-sm text-green-800 flex items-center">
                <span className="mr-2">✓</span>
                Selected:{" "}
                <strong className="ml-1">{businessProposalFile.name}</strong>
              </p>
              <p className="text-xs text-green-600 mt-1">
                Size: {(businessProposalFile.size / (1024 * 1024)).toFixed(2)}{" "}
                MB
              </p>
            </div>
          )}
          {typeof initialData?.business_proposal_file === "string" &&
            initialData.business_proposal_file &&
            !(businessProposalFile instanceof File) && (
              <p className="text-sm text-blue-600 mt-1">
                Current file:{" "}
                <a
                  href={`${process.env.REACT_APP_BASE_URL}${initialData.business_proposal_file}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-800"
                >
                  View / Download
                </a>
              </p>
            )}
          <p className="text-xs text-gray-500 mt-1">
            Accepted formats: PDF, DOC, DOCX (Max size: 1MB)
          </p>
        </div>
      </div>

      <div className="pt-4 flex justify-end gap-3">
        {showNextOnly && !isEditMode && (
          <button
            type="button"
            onClick={handleNextClick}
            className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors disabled:opacity-50"
            disabled={loading || isCheckingCnic}
          >
            {loading ? "Saving..." : isCheckingCnic ? "Validating..." : "Next"}
          </button>
        )}
        {isEditMode && (
          <AddButton
            text="Update"
            type="submit"
            isLoading={loading}
            disabled={loading}
          />
        )}
      </div>
    </form>
  );
};

export default BeneficiaryDetails;
